import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import { WalletedCostReviewInsuranceGroup } from './walleted-cost-review-insurance-group';
import { WalletedCostReviewProviderGroup } from './walleted-cost-review-provider-group';
import { LogService } from '../../services/log-service';
import { CostReviewLineItemBody } from '../../components/cost-review-line-item/cost-review-line-item-body';
import { CostReviewLineItemHeader } from '../../components/cost-review-line-item/cost-review-line-item-header/';
import { CostReviewLineItemSubtotal } from '../../components/cost-review-line-item/cost-review-line-item-subtotal';
import style from '../../components/cost-review-line-item/cost-review-line-item.module.css';
import { CostReviewLineItemPurchaseContainer } from '../../containers/cost-review/cost-review-line-item-purchase-container';
import { useSellerWallets } from '../../hooks/use-seller-wallets';
import { CostReviewHelperService } from '../../services/cost-review-helper-service';
import { CurrencyService } from '../../services/currency-service';
import Global from '../../store/global-selectors';

/**
 * @typedef {import('../../prop-types/cost-review.types').CostReviewWarning} CostReviewWarning
 * @typedef {import('../../prop-types/cost-review.types').CostReviewShippingServiceRecommendationMap} CostReviewShippingServiceRecommendationMap
 * @typedef {import('../../prop-types/cost-review.types').CostReviewSelectedRecommendations} CostReviewSelectedRecommendations
 * @typedef {import('../../prop-types/cost-review.types').CostReviewInsuranceRecommendationMap} CostReviewInsuranceRecommendationMap
 * @typedef {import('../../prop-types/cost-review.types').CostReviewRateShopperRecommendationMap} CostReviewRateShopperRecommendationMap
 */

const useWalletDisplayName = (walletId) => {
  const wallets = useSellerWallets();
  return useMemo(() => {
    const wallet = wallets.find((w) => w.walletId === `${walletId}`);
    if (!wallet) {
      LogService.logError(new Error('Wallet not found in cost review'), { walletId });
      return '';
    }
    return `${wallet.brandName} - ${wallet.nickname}`;
  }, [wallets, walletId]);
};

/**
 * @param {object} props
 * @param {Array<WalletedProviderCostReview>} props.walletProviderGroup
 * @param {(walletId: string, amount: number) => void} props.onPurchaseAmountChange
 * @param {Array<CostReviewWarning>} props.costWarnings
 * @param {boolean} props.readOnlyCostReviewModal
 * @param {CostReviewShippingServiceRecommendationMap} props.shippingServiceRecommendations
 * @param {(recommendationId: string) => void} props.onToggleShippingServiceRecommendation
 * @param {CostReviewSelectedRecommendations} props.selectedShippingServiceRecommendations
 * @param {CostReviewSelectedRecommendations} [props.selectedInsuranceRecommendations]
 * @param {CostReviewInsuranceRecommendationMap} [props.insuranceRecommendations]
 * @param {(recommendationId: string) => void} [props.toggleInsuranceRecommendation]
 * @param {boolean} props.isToggleRecommendationDisabled
 * @param {CostReviewRateShopperRecommendationMap} [props.rateShopperRecommendations]
 * @param {CostReviewSelectedRecommendations} [props.selectedRateShopperRecommendations]
 * @param {(recommendationKey: string) => void} [props.onToggleRateShopperRecommendation]
 * @returns {React.JSX.Element}
 */
export const WalletCostReviewSection = ({
  walletProviderGroup = emptyArray,
  onPurchaseAmountChange = noop,
  costWarnings = emptyArray,
  readOnlyCostReviewModal = false,
  shippingServiceRecommendations = emptyObject,
  onToggleShippingServiceRecommendation = noop,
  selectedShippingServiceRecommendations = emptyObject,
  selectedInsuranceRecommendations = emptyObject,
  insuranceRecommendations = emptyObject,
  toggleInsuranceRecommendation = noop,
  isToggleRecommendationDisabled = false,
  rateShopperRecommendations = emptyObject,
  selectedRateShopperRecommendations = emptyObject,
  onToggleRateShopperRecommendation = noop,
}) => {
  const userHasPurchasingRole = useSelector(Global.selectUserhasPurchasingRole);

  const firstProviderCost = walletProviderGroup[0];

  const { balance, walletId, labelProviderId: firstLabelProviderId } = firstProviderCost;
  const walletSubTotal = walletProviderGroup.reduce((grandSubtotal, prov) => prov.subTotal + grandSubtotal, 0);
  const walletDisplayName = useWalletDisplayName(walletId);
  const flooredBalance = CurrencyService.floorCurrency(balance);

  const shouldShowPurchase = !readOnlyCostReviewModal && balance < walletSubTotal;
  const { errorMessage, minPurchaseAmount, providerErrorMessage } = CostReviewHelperService.costReviewLineItemProviderMeta(
    CostReviewHelperService.getCombinedWalletCosts(walletProviderGroup),
    userHasPurchasingRole,
  );

  const insuranceCosts = walletProviderGroup.filter((providerCost) => providerCost.insuranceProviderId);
  const providerCosts = walletProviderGroup.filter((providerCost) => providerCost.sellerLabelProviderId);

  return (
    <div className={style.container}>
      <CostReviewLineItemHeader
        balance={flooredBalance}
        providerId={firstLabelProviderId}
        showBalance
        walletDisplayName={walletDisplayName}
      />
      <CostReviewLineItemBody>
        {providerCosts.map((providerCost) => {
          const { sellerLabelProviderId } = providerCost;
          const insuranceRecommendation = insuranceRecommendations[sellerLabelProviderId];
          const isInsuranceRecommendationSelected = Boolean(selectedInsuranceRecommendations[insuranceRecommendation?.key]);

          return (
            <WalletedCostReviewProviderGroup
              key={sellerLabelProviderId}
              insuranceRecommendation={insuranceRecommendation}
              isInsuranceRecommendationSelected={isInsuranceRecommendationSelected}
              isToggleRecommendationDisabled={isToggleRecommendationDisabled}
              onToggleRateShopperRecommendation={onToggleRateShopperRecommendation}
              onToggleShippingServiceRecommendation={onToggleShippingServiceRecommendation}
              providerCost={providerCost}
              rateShopperRecommendations={rateShopperRecommendations[sellerLabelProviderId]}
              selectedRateShopperRecommendations={selectedRateShopperRecommendations}
              selectedShippingServiceRecommendations={selectedShippingServiceRecommendations}
              shippingServiceRecommendations={shippingServiceRecommendations[sellerLabelProviderId]}
              toggleInsuranceRecommendation={toggleInsuranceRecommendation}
            />
          );
        })}
        {insuranceCosts.map((insuranceCost) => (
          <WalletedCostReviewInsuranceGroup key={insuranceCost.insuranceProviderId} insuranceCost={insuranceCost} />
        ))}
      </CostReviewLineItemBody>
      <CostReviewLineItemSubtotal subtotal={walletSubTotal} />
      {shouldShowPurchase ? (
        <CostReviewLineItemPurchaseContainer
          costWarnings={costWarnings}
          errorMessage={errorMessage}
          labelProviderId={firstLabelProviderId}
          minPurchaseAmount={minPurchaseAmount}
          onPurchaseAmountChange={onPurchaseAmountChange}
          provider={firstProviderCost}
          providerErrorMessage={providerErrorMessage}
        />
      ) : null}
    </div>
  );
};
